import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullseye, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import AllProjects from "../components/projects/allProjects";

import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/projects.css";

// FAQ Component
const FAQItem = ({ question, answer, isOpen, onClick }) => {
	return (
		<div className="faq-item">
			<button 
				className={`faq-question ${isOpen ? 'active' : ''}`}
				onClick={onClick}
			>
				<span>{question}</span>
				<FontAwesomeIcon 
					icon={isOpen ? faChevronUp : faChevronDown} 
					className="faq-icon"
				/>
			</button>
			{isOpen && (
				<div className="faq-answer">
					<p>{answer}</p>
				</div>
			)}
		</div>
	);
};

const Projects = () => {
	const [openFAQ, setOpenFAQ] = useState(null);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "projects");

	const faqData = [
		{
			question: "What is the normal timeframe for an analysis?",
			answer: "We begin work within 24 hours of the initial consultation. The exact timeline depends on the scope of the project, but most analyses are completed within about one week."
		},
		{
			question: "What is the pricing for your services?",
			answer: "Our rate is $100 per hour, regardless of the type of service selected. A personalized estimate will be provided during your consultation."
		},
		{
			question: "How are payments made?",
			answer: "Payments are processed securely through PayPal. We require 50% upfront before work begins, with the remaining balance due upon delivery of the final analysis."
		},
		{
			question: "Do you guarantee confidentiality?",
			answer: "Yes. All client data, results, and communications are treated with strict confidentiality. To further protect privacy, we ask that any personal identifiers (such as names or contact information) be removed before sharing data with us."
		},
		{
			question: "Do you provide revisions?",
			answer: "Yes. We include one round of revisions in every project to ensure the final analysis fully meets your needs."
		},
		{
			question: "Can you help with study or survey design before data collection?",
			answer: "Absolutely. We provide consultation on experimental design, power analysis, and survey setup to help ensure your study is set up for reliable results."
		},
		{
			question: "What types of data do you accept?",
			answer: "We work with a variety of data formats, including Excel, CSV, R, and SQL databases. If your data is in a different format, we can discuss the best way to transfer it during your consultation."
		}
	];

	const handleFAQClick = (index) => {
		setOpenFAQ(openFAQ === index ? null : index);
	};

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Our Offerings | ${INFO.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta
					name="keywords"
					content={currentSEO.keywords.join(", ")}
				/>
			</Helmet>

			<div className="page-content">
				<NavBar active="projects" />
				<div className="content-wrapper">
					<div className="projects-logo-container">
						<div className="projects-logo">
							<Logo width={46} />
						</div>
					</div>
					<div className="projects-container">
						<div className="title projects-title">
							Our Offerings
						</div>

						<div className="subtitle projects-subtitle">
							We specialize in statistical consulting and operations research, serving organizations and small businesses lacking an in-house statistics department. Our expertise bridges the gap between complex data and actionable insights, helping organizations make informed decisions that drive measurable results.
						</div>

						<div className="premier-specialties-header">
							<div className="section-icon-container">
								<FontAwesomeIcon icon={faBullseye} className="section-icon" />
							</div>
							<div className="title premier-title">
								Premier Specialties
							</div>
						</div>

						<div className="projects-list vertical-layout">
							<AllProjects />
						</div>

						{/* Who We Serve Section */}
						<div className="who-we-serve-section">
							<div className="who-we-serve-header">
								<h2>Who We Serve</h2>
							</div>
							<div className="client-types-grid">
								<div className="client-type">Nonprofits</div>
								<div className="client-type">Hospital Systems</div>
								<div className="client-type">Public Health Departments</div>
								<div className="client-type">Research Labs</div>
								<div className="client-type universities-centered">Universities</div>
								<div className="client-type startups-centered">Startups</div>
							</div>
						</div>

						{/* FAQ Section */}
						<div className="faq-section">
							<div className="faq-header">
								<h2>Frequently Asked Questions</h2>
							</div>
							<div className="faq-list">
								{faqData.map((faq, index) => (
									<FAQItem
										key={index}
										question={faq.question}
										answer={faq.answer}
										isOpen={openFAQ === index}
										onClick={() => handleFAQClick(index)}
									/>
								))}
							</div>
						</div>
					</div>
					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Projects;